/**
 * FE deploy-handover recovery (2026-09-17).
 *
 * THE FAILURE THIS EXISTS FOR. The DEV FE is rebuilt and redeployed on every push that
 * touches `raisy_FE/` (the on-box git-trigger watcher, AGENTS.md §Two environments), and
 * `dist/assets/*` names are content-hashed. Two facts then combine into a dead end:
 *
 *   1. the entry HTML was served with `Cache-Control: max-age=3600` (firebase.dev.json), so a
 *      browser can keep running the PREVIOUS entry for up to an hour without revalidating;
 *   2. a request for a chunk that no longer exists is answered by the SPA rewrite
 *      (`"**" → /index.html`) with **status 200 and content-type text/html** — verified
 *      2026-09-17 with curl: `assets/EmailPage-gC39twzb.js` → `200 text/html`, 7614 bytes,
 *      the app's own `<!doctype html>`.
 *
 * So the tab asks for JavaScript, receives HTML, the module load fails, and the lazy route
 * never arrives: React lands on the app-wide error boundary and the reader sees
 * "Something went wrong / A client error occurred and your request couldn't be completed."
 * The page is not broken and the API is not erroring — the tab is one deploy behind.
 *
 * WHAT THIS DOES. Turns that dead end into a reload, which is the whole remedy: the same
 * request against the current entry yields the current chunk names. It listens on the two
 * signals that carry the failure — Vite's own `vite:preloadError` (fired by `__vitePreload`
 * when a module preload/import fails) and the runtime's message for a failed module script —
 * and reloads **at most once per window** (`RELOAD_WINDOW_MS`) so a genuine, persistent
 * failure cannot become a reload loop; if the reload does not fix it, the next failure is
 * reported instead of retried, and the error boundary stays the honest end state.
 *
 * The hosting header is fixed at its source too (`firebase.dev.json`: the entry revalidates),
 * so this is the second line of defence, not the only one — it covers a tab that was already
 * open across a deploy, which no caching rule can.
 */

/** Session marker + the window that makes the reload happen once, never repeatedly. */
const RELOAD_KEY = "raisy.buildHandoverReloadAt";
const RELOAD_WINDOW_MS = 30_000;

/**
 * The runtime messages for a module that did not arrive. Kept narrow on purpose: an unrelated
 * error must never trigger a reload (reloading on every error would hide real failures).
 */
const CHUNK_FAILURE = new RegExp(
  [
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
    "Importing a module script failed",
    "Failed to load module script",
    "Loading chunk [0-9]+ failed",
  ].join("|"),
  "i",
);

function reloadOnce(reason: string): void {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? "0");
    if (Number.isFinite(last) && Date.now() - last < RELOAD_WINDOW_MS) {
      // Already handed over once and it did not take: report, never loop.
      console.warn(`[FEH_011] build handover did not take (${reason}) — not reloading again`);
      return;
    }
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* storage unavailable (private mode): still reload — a single reload is harmless */
  }
  console.warn(`[FEH_010] this tab is one build behind (${reason}) — reloading once`);
  window.location.reload();
}

export function installDeployHandoverReload(): void {
  window.addEventListener("vite:preloadError", (event) => {
    // Vite dispatches this before rethrowing; the error boundary must not be reached for
    // something a reload resolves.
    (event as Event & { preventDefault?: () => void }).preventDefault?.();
    reloadOnce("vite:preloadError");
  });

  window.addEventListener(
    "error",
    (event) => {
      // Capture phase: a module-script failure surfaces as an error event on the element
      // (or as a message), often before any boundary sees it.
      const message = event instanceof ErrorEvent ? event.message : "";
      if (message && CHUNK_FAILURE.test(message)) reloadOnce("error");
    },
    true,
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";
    if (message && CHUNK_FAILURE.test(message)) {
      event.preventDefault();
      reloadOnce("unhandledrejection");
    }
  });
}
